(() => {
  'use strict';

  class SBmiCalculator extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.form = this.querySelector('[data-bmi-form]');
      this.modal = this.querySelector('[data-bmi-modal]');
      this.modalCard = this.querySelector('.bmi-calculator__modal-card');
      this.closeButtons = this.querySelectorAll('[data-bmi-close]');

      this.petTypeSelect = this.querySelector('[data-pet-type-select]');
      this.petTypeRadios = this.querySelectorAll('[data-bmi-pet-radio]');

      this.dogFields = this.querySelector('[data-dog-fields]');
      this.catFields = this.querySelector('[data-cat-fields]');

      this.dogBreedSelect = this.querySelector('[data-dog-breed-select]');
      this.catBreedSelect = this.querySelector('[data-cat-breed-select]');

      this.formTitle = this.querySelector('[data-bmi-form-title]');
      this.formText = this.querySelector('[data-bmi-form-text]');

      this.errorsContainer = this.querySelector('[data-bmi-errors]');
      this.submitButton = this.querySelector('[data-bmi-submit]');

      this.resultWrapper = this.querySelector('.bmi-calculator__result');
      this.resultLabel = this.querySelector('[data-bmi-result-label]');
      this.resultTitle = this.querySelector('[data-bmi-result-title]');
      this.scoreEl = this.querySelector('[data-bmi-score]');
      this.resultText = this.querySelector('[data-bmi-result-text]');
      this.reportList = this.querySelector('[data-bmi-report]');
      this.recommendations = this.querySelector(
        '[data-bmi-recommendations]'
      );

      this.dogBreedsData = {};
      this.catBreedsData = {};

      this.previouslyFocusedElement = null;

      this.bindEvents();
      this.initialise();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;

      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('bmi-calculator-modal-open');
    }

    bindEvents() {
      if (!this.signal) return;

      this.form?.addEventListener(
        'submit',
        (event) => this.onSubmit(event),
        {
          signal: this.signal
        }
      );

      this.petTypeSelect?.addEventListener(
        'change',
        () => this.onPetTypeSelectChange(),
        {
          signal: this.signal
        }
      );

      this.petTypeRadios.forEach((radio) => {
        radio.addEventListener(
          'change',
          (event) => this.onPetTypeRadioChange(event),
          {
            signal: this.signal
          }
        );
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.closeModal(),
          {
            signal: this.signal
          }
        );
      });

      this.modalCard?.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
        },
        {
          signal: this.signal
        }
      );

      this.form?.querySelectorAll('input, select').forEach((field) => {
        field.addEventListener(
          'input',
          () => this.clearFieldError(field),
          {
            signal: this.signal
          }
        );

        field.addEventListener(
          'change',
          () => this.clearFieldError(field),
          {
            signal: this.signal
          }
        );
      });
    }

    async initialise() {
      const defaultPet = this.getDefaultPetType();

      this.setPetType(defaultPet, {
        dispatchChange: false
      });

      this.onPetTypeChange();
      this.setLoadingState(true);

      await this.loadAllBreeds();

      this.populateBreedDropdown(
        this.dogBreedSelect,
        this.dogBreedsData,
        'Select dog breed'
      );

      this.populateBreedDropdown(
        this.catBreedSelect,
        this.catBreedsData,
        'Select cat breed'
      );

      this.setLoadingState(false);
      this.onPetTypeChange();
    }

    getDefaultPetType() {
      const configuredPet = String(
        this.dataset.defaultPet || ''
      )
        .trim()
        .toLowerCase();

      if (configuredPet === 'dog' || configuredPet === 'cat') {
        return configuredPet;
      }

      const selectedPet = String(
        this.petTypeSelect?.value || ''
      )
        .trim()
        .toLowerCase();

      return selectedPet === 'cat' ? 'cat' : 'dog';
    }

    setLoadingState(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute(
          'aria-busy',
          String(isLoading)
        );
      }

      if (this.dogBreedSelect) {
        this.dogBreedSelect.disabled =
          isLoading || this.getPetType() !== 'dog';
      }

      if (this.catBreedSelect) {
        this.catBreedSelect.disabled =
          isLoading || this.getPetType() !== 'cat';
      }
    }

    async loadAllBreeds() {
      const [dogBreeds, catBreeds] = await Promise.all([
        this.loadBreeds(this.dataset.dogBreedsUrl, 'Dog'),
        this.loadBreeds(this.dataset.catBreedsUrl, 'Cat')
      ]);

      this.dogBreedsData = dogBreeds || {};
      this.catBreedsData = catBreeds || {};
    }

    async loadBreeds(url, type) {
      if (!url) return {};

      try {
        const response = await fetch(url, {
          cache: 'force-cache'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        return data && typeof data === 'object'
          ? data
          : {};
      } catch (error) {
        console.warn(
          `[${type} BMI Calculator] Could not load breeds JSON:`,
          error
        );

        return {};
      }
    }

    populateBreedDropdown(select, breedsData, placeholder) {
      if (!select) return;

      const selectedValue = select.value;

      const breeds = Object.entries(breedsData || {})
        .flatMap(([size, items]) => {
          if (!Array.isArray(items)) return [];

          return items.map((breed) => ({
            breed,
            size
          }));
        })
        .filter((item) => String(item.breed || '').trim())
        .sort((a, b) => {
          return String(a.breed || '').localeCompare(
            String(b.breed || ''),
            undefined,
            {
              sensitivity: 'base'
            }
          );
        });

      select.innerHTML = [
        `<option value="">${this.escapeHtml(placeholder)}</option>`,
        ...breeds.map((item) => {
          const breed = this.escapeHtml(item.breed);
          const size = this.escapeHtml(item.size);

          return `
            <option
              value="${breed}"
              data-size="${size}"
            >
              ${breed}
            </option>
          `;
        })
      ].join('');

      if (selectedValue) {
        select.value = selectedValue;
      }
    }

    onPetTypeRadioChange(event) {
      const petType = String(event.target?.value || '')
        .trim()
        .toLowerCase();

      if (petType !== 'dog' && petType !== 'cat') {
        return;
      }

      this.setPetType(petType, {
        dispatchChange: true
      });
    }

    onPetTypeSelectChange() {
      const petType = this.getPetType();

      this.syncPetTypeRadios(petType);
      this.onPetTypeChange();
    }

    setPetType(
      petType,
      {
        dispatchChange = false
      } = {}
    ) {
      const safePetType = petType === 'cat' ? 'cat' : 'dog';

      if (this.petTypeSelect) {
        this.petTypeSelect.value = safePetType;
      }

      this.syncPetTypeRadios(safePetType);

      if (dispatchChange && this.petTypeSelect) {
        this.petTypeSelect.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      } else {
        this.onPetTypeChange();
      }
    }

    syncPetTypeRadios(petType) {
      this.petTypeRadios.forEach((radio) => {
        radio.checked = radio.value === petType;
      });
    }

    onPetTypeChange() {
      const petType = this.getPetType();

      const isDog = petType === 'dog';
      const isCat = petType === 'cat';

      if (this.dogFields) {
        this.dogFields.hidden = !isDog;
      }

      if (this.catFields) {
        this.catFields.hidden = !isCat;
      }

      this.setFieldsState(this.dogFields, isDog);
      this.setFieldsState(this.catFields, isCat);

      this.clearErrors();

      if (this.formTitle) {
        this.formTitle.textContent = isCat
          ? 'Check your cat’s BMI'
          : 'Check your dog’s BMI';
      }

      if (this.formText) {
        this.formText.textContent = isCat
          ? 'This tool estimates your cat’s BMI using weight, body length, ribcage circumference, hind leg length, breed size, activity level, and body condition indicators.'
          : 'This tool estimates your dog’s BMI using weight, body measurements, breed size, activity level, and body condition indicators.';
      }
    }

    setFieldsState(wrapper, isActive) {
      if (!wrapper) return;

      wrapper
        .querySelectorAll('input, select')
        .forEach((field) => {
          field.disabled = !isActive;

          if (field.dataset.optional === 'true') {
            field.required = false;
            return;
          }

          field.required = isActive;
        });
    }

    getPetType() {
      return String(this.petTypeSelect?.value || 'dog')
        .trim()
        .toLowerCase() === 'cat'
        ? 'cat'
        : 'dog';
    }

    onSubmit(event) {
      event.preventDefault();

      this.clearErrors();

      const validation = this.validateActiveFields();

      if (!validation.isValid) {
        this.renderErrors(validation.errors);

        validation.firstInvalidField?.focus({
          preventScroll: true
        });

        validation.firstInvalidField?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        return;
      }

      const formData = new FormData(this.form);
      const petType = this.getPetType();

      const result =
        petType === 'cat'
          ? this.calculateCatResult(
              this.getCatData(formData)
            )
          : this.calculateDogResult(
              this.getDogData(formData)
            );

      this.renderResult(result);
      this.openModal();
    }

    validateActiveFields() {
      const petType = this.getPetType();
      const activeWrapper =
        petType === 'cat'
          ? this.catFields
          : this.dogFields;

      const errors = [];
      let firstInvalidField = null;

      if (!activeWrapper) {
        return {
          isValid: true,
          errors,
          firstInvalidField
        };
      }

      const fields = Array.from(
        activeWrapper.querySelectorAll(
          'input:not(:disabled), select:not(:disabled)'
        )
      );

      fields.forEach((field) => {
        field.removeAttribute('aria-invalid');

        if (!field.required) {
          return;
        }

        const value = String(field.value || '').trim();
        const label = this.getFieldLabel(field);

        if (!value) {
          errors.push(`${label} is required.`);
          field.setAttribute('aria-invalid', 'true');
          firstInvalidField ||= field;
          return;
        }

        if (
          field.type === 'number' &&
          (!Number.isFinite(Number(value)) ||
            Number(value) <= 0)
        ) {
          errors.push(
            `Enter a valid value for ${label.toLowerCase()}.`
          );

          field.setAttribute('aria-invalid', 'true');
          firstInvalidField ||= field;
        }
      });

      return {
        isValid: errors.length === 0,
        errors,
        firstInvalidField
      };
    }

    getFieldLabel(field) {
      const fieldWrapper = field.closest(
        '.bmi-calculator__field'
      );

      const label = fieldWrapper?.querySelector(
        ':scope > span'
      );

      return String(
        label?.textContent ||
          field.name ||
          'This field'
      ).trim();
    }

    clearFieldError(field) {
      field?.removeAttribute('aria-invalid');

      if (!this.errorsContainer) {
        return;
      }

      const hasInvalidFields = this.form?.querySelector(
        '[aria-invalid="true"]'
      );

      if (!hasInvalidFields) {
        this.clearErrors();
      }
    }

    renderErrors(errors) {
      if (!this.errorsContainer) {
        return;
      }

      this.errorsContainer.innerHTML = errors
        .map(
          (error) =>
            `<div>${this.escapeHtml(error)}</div>`
        )
        .join('');

      this.errorsContainer.hidden = false;
    }

    clearErrors() {
      if (this.errorsContainer) {
        this.errorsContainer.innerHTML = '';
        this.errorsContainer.hidden = true;
      }

      this.form
        ?.querySelectorAll('[aria-invalid="true"]')
        .forEach((field) => {
          field.removeAttribute('aria-invalid');
        });
    }

    getDogData(formData) {
      const breed = String(
        formData.get('dogBreed') || ''
      ).trim();

      return {
        petType: 'dog',
        breed,
        age: Number(formData.get('dogAge') || 0),
        weight: Number(formData.get('dogWeight') || 0),
        height: Number(formData.get('dogHeight') || 0),
        length: Number(formData.get('dogLength') || 0),
        activity: String(
          formData.get('dogActivity') || ''
        ),
        ribs: String(formData.get('dogRibs') || ''),
        waist: String(formData.get('dogWaist') || ''),
        belly: String(formData.get('dogBelly') || ''),
        breedSize: this.getBreedSizeGroup(
          breed,
          this.dogBreedsData
        )
      };
    }

    getCatData(formData) {
      const breed = String(
        formData.get('catBreed') || ''
      ).trim();

      return {
        petType: 'cat',
        breed,
        age: Number(formData.get('catAge') || 0),
        weight: Number(formData.get('catWeight') || 0),
        length: Number(formData.get('catLength') || 0),
        ribcage: Number(
          formData.get('catRibcage') || 0
        ),
        hindLeg: Number(
          formData.get('catHindLeg') || 0
        ),
        activity: String(
          formData.get('catActivity') || ''
        ),
        ribs: String(formData.get('catRibs') || ''),
        waist: String(formData.get('catWaist') || ''),
        belly: String(formData.get('catBelly') || ''),
        breedSize: this.getBreedSizeGroup(
          breed,
          this.catBreedsData
        )
      };
    }

    calculateDogResult(data) {
      const bmiIndex = this.calculateDogBmiIndex(data);
      let assessmentScore = 5;

      assessmentScore += this.getSharedVisualScore(data);
      assessmentScore +=
        this.getDogProportionAdjustment(data);

      assessmentScore = Math.max(
        1,
        Math.min(9, Math.round(assessmentScore))
      );

      const content =
        this.getDogCategoryContent(assessmentScore);

      return {
        ...data,
        assessmentScore,
        bmiIndex,
        ...content,
        report: this.buildDogReport(
          data,
          assessmentScore,
          content.category,
          bmiIndex
        ),
        recommendations:
          this.buildDogRecommendations(
            data,
            assessmentScore
          )
      };
    }

    calculateCatResult(data) {
      const bmiIndex = this.calculateCatBmiIndex(data);
      let assessmentScore = 5;

      assessmentScore += this.getSharedVisualScore(data);
      assessmentScore +=
        this.getCatProportionAdjustment(data);

      assessmentScore = Math.max(
        1,
        Math.min(9, Math.round(assessmentScore))
      );

      const content =
        this.getCatCategoryContent(assessmentScore);

      return {
        ...data,
        assessmentScore,
        bmiIndex,
        ...content,
        report: this.buildCatReport(
          data,
          assessmentScore,
          content.category,
          bmiIndex
        ),
        recommendations:
          this.buildCatRecommendations(
            data,
            assessmentScore
          )
      };
    }

    getSharedVisualScore(data) {
      const ribScores = {
        very_visible: -3,
        easy_to_feel: -1,
        slightly_padded: 0,
        hard_to_feel: 2,
        not_feelable: 3
      };

      const waistScores = {
        very_tucked: -2,
        visible: 0,
        slight: 1,
        none: 2,
        rounded: 3
      };

      const bellyScores = {
        strong_tuck: -2,
        normal_tuck: 0,
        minimal_tuck: 1,
        straight: 2,
        hanging: 3
      };

      const activityScores = {
        low: 1,
        normal: 0,
        high: -1
      };

      return (
        (ribScores[data.ribs] || 0) +
        (waistScores[data.waist] || 0) +
        (bellyScores[data.belly] || 0) +
        (activityScores[data.activity] || 0)
      );
    }

    calculateDogBmiIndex(data) {
      if (
        !data.weight ||
        !data.height ||
        !data.length
      ) {
        return '';
      }

      const heightM = data.height / 100;
      const lengthM = data.length / 100;

      if (!heightM || !lengthM) {
        return '';
      }

      return (
        data.weight /
        (heightM * lengthM)
      ).toFixed(1);
    }

    calculateCatBmiIndex(data) {
      if (
        !data.weight ||
        !data.length ||
        !data.ribcage ||
        !data.hindLeg
      ) {
        return '';
      }

      const frameLengthM =
        (data.length + data.hindLeg) / 100;

      const ribcageM = data.ribcage / 100;

      if (!frameLengthM || !ribcageM) {
        return '';
      }

      return (
        data.weight /
        (frameLengthM * ribcageM)
      ).toFixed(1);
    }

    getDogProportionAdjustment(data) {
      const bmiIndex = Number(
        this.calculateDogBmiIndex(data)
      );

      if (!bmiIndex) return 0;

      if (bmiIndex < 30) return -2;
      if (bmiIndex < 36) return -1;
      if (bmiIndex > 100) return 2;
      if (bmiIndex > 76) return 1;

      return 0;
    }

    getCatProportionAdjustment(data) {
      const bmiIndex = Number(
        this.calculateCatBmiIndex(data)
      );

      if (
        !bmiIndex ||
        !data.length ||
        !data.ribcage ||
        !data.hindLeg
      ) {
        return 0;
      }

      const ribcageRatio =
        data.ribcage / data.length;

      const legRatio =
        data.hindLeg / data.length;

      let adjustment = 0;

      if (bmiIndex < 115) adjustment -= 1;
      if (bmiIndex > 165) adjustment += 1;
      if (bmiIndex > 205) adjustment += 1;

      if (ribcageRatio < 0.42) adjustment -= 1;
      if (ribcageRatio > 0.56) adjustment += 1;
      if (ribcageRatio > 0.64) adjustment += 1;

      if (
        legRatio < 0.28 &&
        data.weight > 5.5
      ) {
        adjustment += 1;
      }

      if (
        legRatio > 0.42 &&
        data.weight < 3.5
      ) {
        adjustment -= 1;
      }

      if (
        data.breedSize === 'large' &&
        data.weight > 7.5 &&
        ribcageRatio < 0.58
      ) {
        adjustment -= 1;
      }

      if (
        data.breedSize === 'small' &&
        data.weight > 5.5
      ) {
        adjustment += 1;
      }

      return Math.max(
        -2,
        Math.min(2, adjustment)
      );
    }

    getDogCategoryContent(score) {
      if (score <= 2) {
        return {
          category: 'Very underweight',
          title: 'Very low BMI assessment',
          summary:
            'Your dog appears to be well below an ideal body range. The submitted details suggest low body reserves, which may require a closer look at diet, appetite, digestion, and overall health.'
        };
      }

      if (score === 3) {
        return {
          category: 'Underweight',
          title: 'Below ideal BMI assessment',
          summary:
            'Your dog may be slightly below ideal condition. This can happen in very active dogs, young growing dogs, or dogs with insufficient calorie intake.'
        };
      }

      if (score >= 4 && score <= 5) {
        return {
          category: 'Ideal',
          title: 'Ideal BMI assessment',
          summary:
            'Your dog appears to be within a healthy body range. The goal is to keep ribs easy to feel, waist visible from above, and abdominal tuck present from the side.'
        };
      }

      if (score === 6) {
        return {
          category: 'Slightly overweight',
          title: 'Slightly above ideal BMI assessment',
          summary:
            'Your dog may be starting to carry extra body fat. This is a good stage to make small adjustments before weight becomes harder to manage.'
        };
      }

      if (score === 7) {
        return {
          category: 'Overweight',
          title: 'Above ideal BMI assessment',
          summary:
            'Your dog appears to be above ideal range. Ribs may be harder to feel, the waist may be less defined, and movement or endurance may be affected over time.'
        };
      }

      return {
        category: 'Obese',
        title: 'High BMI assessment',
        summary:
          'Your dog may be significantly above ideal range. A structured, gradual weight-management plan is recommended, ideally with veterinary guidance.'
      };
    }

    getCatCategoryContent(score) {
      if (score <= 2) {
        return {
          category: 'Very underweight',
          title: 'Very low BMI assessment',
          summary:
            'Your cat appears to be well below an ideal body range. The submitted details suggest low body reserves, which may require a closer look at diet, appetite, digestion, and overall health.'
        };
      }

      if (score === 3) {
        return {
          category: 'Underweight',
          title: 'Below ideal BMI assessment',
          summary:
            'Your cat may be slightly below ideal condition. This can happen in very active cats, young growing cats, or cats with insufficient calorie intake.'
        };
      }

      if (score >= 4 && score <= 5) {
        return {
          category: 'Ideal',
          title: 'Ideal BMI assessment',
          summary:
            'Your cat appears to be within a healthy body range. The goal is to keep ribs easy to feel, waist visible from above, and belly fat pad modest.'
        };
      }

      if (score === 6) {
        return {
          category: 'Slightly overweight',
          title: 'Slightly above ideal BMI assessment',
          summary:
            'Your cat may be starting to carry extra body fat. This is a good stage to make small adjustments before weight becomes harder to manage.'
        };
      }

      if (score === 7) {
        return {
          category: 'Overweight',
          title: 'Above ideal BMI assessment',
          summary:
            'Your cat appears to be above ideal range. Ribs may be harder to feel, the waist may be less defined, and the belly pad may look heavier than ideal.'
        };
      }

      return {
        category: 'Obese',
        title: 'High BMI assessment',
        summary:
          'Your cat may be significantly above ideal range. A structured, gradual weight-management plan is recommended, ideally with veterinary guidance.'
      };
    }

    getBreedSizeGroup(breed, breedsData) {
      const breedKey = this.normalizeKey(breed);

      if (!breedKey) return '';

      for (const [size, breeds] of Object.entries(
        breedsData || {}
      )) {
        const found = (breeds || []).some((item) => {
          return (
            this.normalizeKey(item) === breedKey
          );
        });

        if (found) return size;
      }

      return '';
    }

    getDogBreedSizeLabel(size) {
      const labels = {
        small: 'small',
        medium: 'medium-sized',
        large: 'large',
        extra_large: 'extra-large'
      };

      return labels[size] || '';
    }

    getCatBreedSizeLabel(size) {
      const labels = {
        small: 'small',
        medium: 'medium-sized',
        large: 'large'
      };

      return labels[size] || '';
    }

    getDogBreedInsight(data) {
      if (data.breedSize === 'small') {
        return 'This breed is usually considered small. Small dogs can gain weight quickly because even small treat portions can make a noticeable difference.';
      }

      if (data.breedSize === 'medium') {
        return 'This breed is usually considered medium-sized. BMI should still be interpreted together with ribs, waist, abdominal tuck, and activity level rather than weight alone.';
      }

      if (data.breedSize === 'large') {
        return 'This breed is usually considered large. Keeping large dogs lean helps reduce pressure on joints, mobility, stamina, and long-term comfort.';
      }

      if (data.breedSize === 'extra_large') {
        return 'This breed is usually considered extra-large. Weight control is especially important because extra body fat can add stress to joints, spine, hips, elbows, and movement.';
      }

      return 'Breed type can affect ideal weight range, muscle mass, chest depth, coat thickness, and natural body shape.';
    }

    getCatBreedInsight(data) {
      if (data.breedSize === 'small') {
        return 'This breed is usually considered small. Small cats can show weight changes quickly, so rib feel, waist shape, and belly fat pad are especially useful.';
      }

      if (data.breedSize === 'medium') {
        return 'This breed is usually considered medium-sized. BMI should be interpreted together with rib feel, waist shape, ribcage circumference, and belly fat pad.';
      }

      if (data.breedSize === 'large') {
        return 'This breed is usually considered large. Large cats can naturally weigh more, so BMI and frame measurements are more useful than weight alone.';
      }

      return 'Breed type can affect frame size, coat volume, body length, ribcage size, and expected weight range.';
    }

    getDogMeasurementInsight(data) {
      const bmiIndex = Number(
        this.calculateDogBmiIndex(data)
      );

      if (!bmiIndex) return '';

      if (bmiIndex < 30) {
        return 'The entered measurements suggest a very light body mass relative to height and body length.';
      }

      if (bmiIndex < 36) {
        return 'The entered measurements suggest a lean body build relative to height and body length.';
      }

      if (bmiIndex > 100) {
        return 'The entered measurements suggest a high body mass relative to height and body length.';
      }

      if (bmiIndex > 76) {
        return 'The entered measurements suggest a slightly heavier build relative to height and body length.';
      }

      return 'The entered measurements appear broadly consistent with a balanced adult dog body shape.';
    }

    getCatMeasurementInsight(data) {
      const bmiIndex = Number(
        this.calculateCatBmiIndex(data)
      );

      if (!bmiIndex) return '';

      if (bmiIndex < 115) {
        return 'The entered measurements suggest a relatively light body mass for the submitted frame measurements.';
      }

      if (bmiIndex > 205) {
        return 'The entered measurements suggest a high body mass relative to body length, ribcage circumference, and hind leg length.';
      }

      if (bmiIndex > 165) {
        return 'The entered measurements suggest a moderately heavier body build relative to the submitted frame measurements.';
      }

      return 'The entered measurements appear broadly consistent with a balanced adult cat body shape.';
    }

    buildDogReport(
      data,
      assessmentScore,
      category,
      bmiIndex
    ) {
      const report = [];

      if (bmiIndex) {
        report.push(`BMI index: ${bmiIndex}.`);
      }

      report.push(`BMI assessment: ${category}.`);

      if (data.breed) {
        const sizeLabel =
          this.getDogBreedSizeLabel(
            data.breedSize
          );

        if (sizeLabel) {
          report.push(
            `Breed entered: ${data.breed}. This is detected as a ${sizeLabel} breed.`
          );
        } else {
          report.push(
            `Breed entered: ${data.breed}. Breed size was not found in the current dog breed list.`
          );
        }

        report.push(this.getDogBreedInsight(data));
      }

      if (
        data.weight &&
        data.height &&
        data.length
      ) {
        report.push(
          `Entered measurements: ${data.weight} kg, ${data.height} cm shoulder height, ${data.length} cm body length.`
        );

        report.push(
          this.getDogMeasurementInsight(data)
        );
      }

      if (data.age <= 1) {
        report.push(
          'Because your dog is still young, this result should be interpreted together with growth stage, breed size, and expected adult development.'
        );
      } else if (data.age >= 7) {
        report.push(
          'Because your dog is senior or approaching senior age, it is worth monitoring weight, muscle condition, mobility, joints, and daily comfort more closely.'
        );
      } else {
        report.push(
          'At this adult age, maintaining a stable body range is usually easier than correcting weight gain later.'
        );
      }

      this.addSharedReportLines(
        report,
        data,
        'dog'
      );

      if (assessmentScore >= 6) {
        report.push(
          'For dogs above ideal range, even small weight increases can affect stamina, joint comfort, heat tolerance, and long-term mobility.'
        );
      }

      if (assessmentScore <= 3) {
        report.push(
          'For dogs below ideal range, it is important to check appetite, digestion, stool quality, energy level, and any recent unexplained weight loss.'
        );
      }

      report.push(
        'This result is a general wellness guide and should not replace veterinary advice.'
      );

      return report.filter(Boolean);
    }

    buildCatReport(
      data,
      assessmentScore,
      category,
      bmiIndex
    ) {
      const report = [];

      if (bmiIndex) {
        report.push(`BMI index: ${bmiIndex}.`);
      }

      report.push(`BMI assessment: ${category}.`);

      if (data.breed) {
        const sizeLabel =
          this.getCatBreedSizeLabel(
            data.breedSize
          );

        if (sizeLabel) {
          report.push(
            `Breed entered: ${data.breed}. This is detected as a ${sizeLabel} cat breed.`
          );
        } else {
          report.push(
            `Breed entered: ${data.breed}. Breed size was not found in the current cat breed list.`
          );
        }

        report.push(this.getCatBreedInsight(data));
      }

      if (
        data.weight &&
        data.length &&
        data.ribcage &&
        data.hindLeg
      ) {
        report.push(
          `Entered measurements: ${data.weight} kg, ${data.length} cm body length, ${data.ribcage} cm ribcage circumference, ${data.hindLeg} cm hind leg length.`
        );

        report.push(
          this.getCatMeasurementInsight(data)
        );
      }

      if (data.age <= 1) {
        report.push(
          'Young cats are still growing, so this result should be interpreted together with age, growth stage, and expected adult size.'
        );
      } else if (data.age >= 10) {
        report.push(
          'Senior cats benefit from regular weight tracking, muscle condition checks, appetite monitoring, and veterinary wellness reviews.'
        );
      } else {
        report.push(
          'For an adult cat, stable weight and consistent body range are usually easier to maintain than correct later.'
        );
      }

      if (data.ribcage && data.length) {
        const ribcageRatio =
          data.ribcage / data.length;

        if (ribcageRatio < 0.42) {
          report.push(
            'The ribcage-to-length ratio looks relatively narrow, which can support a leaner body reading.'
          );
        } else if (ribcageRatio > 0.64) {
          report.push(
            'The ribcage-to-length ratio looks high, which may suggest excess body mass around the chest and trunk.'
          );
        } else if (ribcageRatio > 0.56) {
          report.push(
            'The ribcage-to-length ratio is moderately elevated, so waist definition and rib feel are especially important.'
          );
        } else {
          report.push(
            'The ribcage-to-length ratio appears broadly balanced for a typical adult cat.'
          );
        }
      }

      if (data.hindLeg && data.length) {
        const legRatio =
          data.hindLeg / data.length;

        if (legRatio < 0.28) {
          report.push(
            'The hind leg measurement appears relatively short compared with body length, so weight may have a stronger effect on the final assessment.'
          );
        } else if (legRatio > 0.42) {
          report.push(
            'The hind leg measurement suggests a longer frame, so total weight should be interpreted together with body shape and rib feel.'
          );
        } else {
          report.push(
            'The hind leg measurement appears proportionate to body length.'
          );
        }
      }

      this.addSharedReportLines(
        report,
        data,
        'cat'
      );

      if (assessmentScore >= 6) {
        report.push(
          'For cats above ideal range, gradual changes are important. Rapid weight loss can be unsafe, especially in overweight cats.'
        );
      }

      if (assessmentScore <= 3) {
        report.push(
          'For cats below ideal range, appetite, digestion, stool quality, energy level, and unexplained weight loss should be monitored closely.'
        );
      }

      report.push(
        'This assessment is a general wellness guide and should not replace veterinary advice.'
      );

      return report.filter(Boolean);
    }

    addSharedReportLines(report, data, petType) {
      const animal =
        petType === 'cat' ? 'cat' : 'dog';

      if (data.ribs === 'very_visible') {
        report.push(
          `Clearly visible ribs can suggest the ${animal} is below ideal condition or has low fat reserves.`
        );
      } else if (data.ribs === 'easy_to_feel') {
        report.push(
          'Ribs that are easy to feel are usually healthy when the waist and belly shape also look healthy.'
        );
      } else if (
        data.ribs === 'slightly_padded'
      ) {
        report.push(
          'Slightly padded ribs can still be normal if the waist remains visible and the body shape is balanced.'
        );
      } else if (
        data.ribs === 'hard_to_feel' ||
        data.ribs === 'not_feelable'
      ) {
        report.push(
          'Ribs that are hard to feel often indicate excess fat coverage.'
        );
      }

      if (data.waist === 'very_tucked') {
        report.push(
          'A very pronounced waist may suggest a lean or underweight condition, especially when combined with visible ribs.'
        );
      } else if (data.waist === 'visible') {
        report.push(
          'A visible waist from above supports a healthy body range.'
        );
      } else if (data.waist === 'slight') {
        report.push(
          'A slight waist can be acceptable, but it should be interpreted together with rib feel and overall body shape.'
        );
      } else if (
        data.waist === 'none' ||
        data.waist === 'rounded'
      ) {
        report.push(
          'A reduced waist or rounded body shape often suggests excess body fat.'
        );
      }

      if (data.belly === 'strong_tuck') {
        report.push(
          petType === 'cat'
            ? 'A very tucked belly can support a leaner body reading, especially if ribs are visible.'
            : 'A strong abdominal tuck can be normal in athletic dogs but may suggest low reserves if ribs are very visible.'
        );
      } else if (
        data.belly === 'normal_tuck'
      ) {
        report.push(
          'A normal belly line supports a healthy body range.'
        );
      } else if (
        data.belly === 'minimal_tuck'
      ) {
        report.push(
          petType === 'cat'
            ? 'A slight belly pad can be normal in cats, but it should be monitored together with waist and rib feel.'
            : 'A minimal abdominal tuck can be an early sign that extra fat is starting to build.'
        );
      } else if (data.belly === 'straight') {
        report.push(
          'A straight underside can suggest reduced tuck and possible extra body fat.'
        );
      } else if (data.belly === 'hanging') {
        report.push(
          petType === 'cat'
            ? 'A hanging belly or pronounced fat pad can be associated with overweight condition, especially when ribs are hard to feel.'
            : 'A hanging belly or heavy underside usually suggests excess fat and reduced body balance.'
        );
      }

      if (data.activity === 'low') {
        report.push(
          petType === 'cat'
            ? 'Indoor or low-activity cats can gain weight more easily if food portions and treats are not controlled.'
            : 'Low activity means food portions and treats should be managed carefully to avoid gradual weight gain.'
        );
      } else if (data.activity === 'normal') {
        report.push(
          'Average activity is suitable for maintenance if meals, treats, and exercise stay consistent.'
        );
      } else if (data.activity === 'high') {
        report.push(
          petType === 'cat'
            ? 'Very active or outdoor cats may need more calories, hydration, and recovery support.'
            : 'High activity can increase calorie and recovery needs, but it does not always prevent weight gain if treats or portions are too high.'
        );
      }
    }

    buildDogRecommendations(
      data,
      assessmentScore
    ) {
      const breed =
        String(data.breed || 'your dog').trim() ||
        'your dog';

      if (assessmentScore <= 2) {
        return `${breed} appears to be very lean based on the submitted details. Review daily calorie intake, appetite, stool quality, recent illness, parasite prevention, and unexplained weight loss. Avoid rapid overfeeding; gradual nutritional support and veterinary advice are recommended.`;
      }

      if (assessmentScore === 3) {
        return `${breed} may benefit from slightly improved nutritional support. Consider reviewing food quantity, meal frequency, protein quality, activity level, and whether weight has been stable. If the dog is very active, calorie needs may simply be higher.`;
      }

      if (
        assessmentScore >= 4 &&
        assessmentScore <= 5
      ) {
        return `${breed} appears to be in a healthy range. Maintain the current routine, keep treats controlled, check ribs and waist monthly, and track weight trends.`;
      }

      if (assessmentScore === 6) {
        return `${breed} may be slightly above ideal range. Start with small changes: reduce treats, avoid table scraps, measure meals more accurately, and add a little more daily movement.`;
      }

      if (assessmentScore === 7) {
        return `${breed} is likely carrying extra weight. Focus on controlled portions, lower-calorie treats, structured walks, play sessions, and weekly weight tracking.`;
      }

      return `${breed} may need a structured weight-management plan. Use measured portions, reduce calorie-dense extras, choose low-impact activity, track progress consistently, and speak with a veterinarian before making major diet changes.`;
    }

    buildCatRecommendations(
      data,
      assessmentScore
    ) {
      const breed =
        String(data.breed || 'your cat').trim() ||
        'your cat';

      if (assessmentScore <= 2) {
        return `${breed} appears to be very lean based on the submitted details. Monitor appetite, meal intake, stool quality, energy, and recent weight change. If weight loss is unexplained or ribs are clearly visible, veterinary advice is recommended.`;
      }

      if (assessmentScore === 3) {
        return `${breed} may be slightly below ideal range. Review food intake, appetite, activity, and whether weight has been stable. If your cat is active and otherwise healthy, calorie needs may simply be higher.`;
      }

      if (
        assessmentScore >= 4 &&
        assessmentScore <= 5
      ) {
        return `${breed} appears to be in a healthy range. Maintain a balanced diet, regular play, enrichment, monthly body checks, and routine weight tracking.`;
      }

      if (assessmentScore === 6) {
        return `${breed} may be slightly above ideal range. Start with small changes: measure meals, reduce treats, add short daily play sessions, and recheck body condition in a few weeks.`;
      }

      if (assessmentScore === 7) {
        return `${breed} is likely carrying extra weight. Consider controlled portions, fewer treats, more daily play, puzzle feeders, climbing opportunities, and regular weight tracking.`;
      }

      return `${breed} should follow a structured weight-management plan. Focus on calorie control, gradual activity increase, enrichment, regular progress tracking, and veterinary supervision. Avoid rapid weight loss.`;
    }

    renderResult(result) {
      if (this.resultLabel) {
        this.resultLabel.textContent =
          result.petType === 'cat'
            ? 'Cat BMI result'
            : 'Dog BMI result';
      }

      if (this.resultTitle) {
        this.resultTitle.textContent = result.title;
      }

      if (this.scoreEl) {
        this.scoreEl.textContent =
          result.bmiIndex || result.category;
      }

      if (this.resultText) {
        this.resultText.textContent =
          result.summary;
      }

      if (this.reportList) {
        this.reportList.innerHTML = result.report
          .map(
            (item) =>
              `<li>${this.escapeHtml(item)}</li>`
          )
          .join('');
      }

      if (this.recommendations) {
        this.recommendations.textContent =
          result.recommendations;
      }

      if (this.resultWrapper) {
        this.resultWrapper.dataset.status =
          this.getResultStatus(result);
      }
    }

    getResultStatus(result) {
      const category = String(
        result.category || ''
      ).toLowerCase();

      if (category === 'ideal') {
        return 'ideal';
      }

      if (
        category.includes('underweight') ||
        category.includes('slightly')
      ) {
        return 'warning';
      }

      if (
        category.includes('overweight') ||
        category.includes('obese')
      ) {
        return 'danger';
      }

      return '';
    }

    openModal() {
      if (!this.modal) return;

      this.previouslyFocusedElement =
        document.activeElement;

      this.modal.classList.add('is-open');
      this.modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document.body.classList.add(
        'bmi-calculator-modal-open'
      );

      document.addEventListener(
        'keydown',
        this.onKeydown
      );

      requestAnimationFrame(() => {
        const closeButton = this.modal.querySelector(
          '.bmi-calculator__modal-close'
        );

        closeButton?.focus({
          preventScroll: true
        });
      });
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document.body.classList.remove(
        'bmi-calculator-modal-open'
      );

      document.removeEventListener(
        'keydown',
        this.onKeydown
      );

      if (
        this.previouslyFocusedElement instanceof
        HTMLElement
      ) {
        this.previouslyFocusedElement.focus({
          preventScroll: true
        });
      }

      this.previouslyFocusedElement = null;
    }

    onKeydown = (event) => {
      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }

      if (
        event.key === 'Tab' &&
        this.modal?.classList.contains('is-open')
      ) {
        this.trapModalFocus(event);
      }
    };

    trapModalFocus(event) {
      const focusableElements = Array.from(
        this.modal.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        )
      ).filter((element) => {
        return (
          !element.hasAttribute('hidden') &&
          element.offsetParent !== null
        );
      });

      if (!focusableElements.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[
          focusableElements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    normalizeKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replaceAll('ã¶', 'ö')
        .replaceAll('ã©', 'é')
        .replaceAll('â€™', '’')
        .replaceAll('â€˜', '‘')
        .replaceAll('â€“', '–')
        .replaceAll('â€”', '—')
        .replace(/\s+/g, ' ');
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  if (!customElements.get('s-bmi-calculator')) {
    customElements.define(
      's-bmi-calculator',
      SBmiCalculator
    );
  }
})();
